// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import updateSelectedUom from "@ces/App/Handlers/CommonHandlers/UpdateSelectedUom";
import { CustomAppState } from "@ces/App/Store/Reducers";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import {
    HasProductContext,
    ProductContextModel,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

interface OwnProps extends SelectProps {
    labelOverride?: React.ReactNode;
    disabled?: boolean;
    extendedStyles?: SelectPresentationProps;
    isDefaultBehavior?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    Omit<HasProductContext, "productContext"> & {
        productContext: Omit<ProductContextModel, "productInfo"> & {
            productInfo: ProductInfo & { selectedUOMLineId?: string };
        };
    } & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomAppState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    // XNG-Changes
    selectedUomByProductId: state.xSelectedUom.selectedUomByProductId,
    quickOrder: getCurrentPage(state).type === "QuickOrderPage",
});

// XNG-Changes
const mapDispatchToProps = {
    updateSelectedUom,
};

export const productUnitOfMeasureSelectStyles: SelectPresentationProps = {};

const ProductUnitOfMeasureSelect: React.FC<Props> = ({
    productContext: {
        product: { unitOfMeasures },
        productInfo: { unitOfMeasure, productId, qtyOrdered, selectedUOMLineId },
        onUnitOfMeasureChanged,
        // XNG-Changes
        onQtyOrderedChanged,
    },
    disabled,
    productSettings,
    labelOverride,
    extendedStyles,
    // XNG-Changes
    updateSelectedUom,
    selectedUomByProductId,
    isDefaultBehavior,
    quickOrder,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeToNew(productUnitOfMeasureSelectStyles, extendedStyles));
    const dispatch = useDispatch();
    // XNG-Changes - exclude uom.isDefault from options
    const uoms = React.useMemo(() => unitOfMeasures?.filter(uom => !uom.isDefault) ?? [], [unitOfMeasures]);

    // XNG-Changes - update qty based on qtyPerBaseUnitOfMeasure for the first uom in the dropdown
    useEffect(() => {
        if (uoms.length && !isDefaultBehavior) {
            const { unitOfMeasure: defaultUnitOfMeasure, qtyPerBaseUnitOfMeasure = 1 } = uoms[0];
            updateSelectedUom({
                productId: selectedUOMLineId || productId,
                selectedUom: defaultUnitOfMeasure,
                multiplesOf: qtyPerBaseUnitOfMeasure,
                quickOrder,
            });
            setTimeout(() => {
                onUnitOfMeasureChanged?.(defaultUnitOfMeasure);
            }, 0);
            onQtyOrderedChanged?.(qtyPerBaseUnitOfMeasure);
        } else if (uoms.length) {
            const findSelectedUom = uoms.find(uom => uom.unitOfMeasure === unitOfMeasure) || uoms[0];
            updateSelectedUom({
                productId: selectedUOMLineId || productId,
                selectedUom: unitOfMeasure,
                multiplesOf: findSelectedUom?.qtyPerBaseUnitOfMeasure || 1,
                quickOrder,
            });
        }
    }, [uoms]);

    // XNG-Changes
    useEffect(() => {
        if (!quickOrder) {
            return () => {
                dispatch({
                    type: "xUpdateSelectedUom/resetSelectedUom",
                    productId: selectedUOMLineId || productId,
                });
            };
        }
    }, []);

    // XNG-Changes - use uoms to check
    if (!productSettings?.alternateUnitsOfMeasure || uoms.length <= 1) {
        return null;
    }

    const uomChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        // XNG-Changes - CES-816
        // Update qtyOrdered directly based on selected UOM.
        const value = event.target.value;
        const selected = uoms.find(uom => uom.unitOfMeasure === value);
        const qty = selected?.qtyPerBaseUnitOfMeasure;

        updateSelectedUom({
            productId: selectedUOMLineId || productId,
            selectedUom: value,
            multiplesOf: qty ?? 1,
            quickOrder,
        });
        // XNG-Changes
        onQtyOrderedChanged?.(qty ?? qtyOrdered);

        if (!onUnitOfMeasureChanged) {
            logger.warn(`There was no onUnitOfMeasureChanged passed to the ProductContext for ${productId}`);
            return;
        }
        onUnitOfMeasureChanged(value);
    };

    return (
        <Select
            label={labelOverride ?? translate("U/M")}
            // XNG-Changes - use selectedUom state
            value={unitOfMeasure}
            onChange={uomChangeHandler}
            disabled={disabled}
            data-test-selector="product_unitOfMeasureSelect"
            {...styles}
            {...otherProps}
        >
            {/* XNG-Changes - Remove qtyPerBaseUnitOfMeasure */}
            {uoms
                .filter(uom => !uom.isDefault)
                .map(uom => (
                    <option key={uom.id.toString()} value={uom.unitOfMeasure}>
                        {uom.description ? uom.description : uom.unitOfMeasureDisplay}{" "}
                        {/* {uom.qtyPerBaseUnitOfMeasure !== 1 ? `/${uom.qtyPerBaseUnitOfMeasure}` : ""} */}
                    </option>
                ))}
        </Select>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductUnitOfMeasureSelect));
